import styled from 'styled-components';
import { FlexColumn, FlexRow, PageTile, SvgIcon, Text } from '@make-software/cspr-design';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { DealsRequestForm } from 'app/components/management/apps/deals/join/deals-request-form';
import { SkeletonLoader } from 'app/components/common/skeleton-loader/skeleton-loader';
import {
  getExtendedOffersById,
  ExtendedOffersResponse
} from 'app/console-api/offers/offers-extended';
import { Breadcrumbs } from 'app/components/common/breadcrumbs/breadcrumbs';
import { HttpError } from 'app/components/utils/http-error';
import { AUTH_TOKEN, customErrorMessage, statusCode } from 'app/constants';
import { SIGN_IN_PATH } from 'app/route/paths';
import { ErrorResult } from 'app/organizations-api/types';
import { ErrorWindow } from 'app/components/common/error-window/error-window';
import NotFoundIcon from 'assets/logos/no-found.svg';

const Container = styled(FlexColumn)(({ theme }) =>
  theme.withMedia({
    margin: ['unset', '0 40px 0 40px', '0 40px 0 40px']
  })
);

const FormContainer = styled(FlexRow)(({ theme }) =>
  theme.withMedia({
    margin: '48px'
  })
);

const LoadingContainer = styled.div(({ theme }) =>
  theme.withMedia({
    minWidth: [200, 300, 500],
    '& > * + *': {
      marginTop: [28]
    }
  })
);

export const DealsRequestPage = () => {
  const { id } = useParams<{ id: string }>();
  const [offers, setOffers] = useState<ExtendedOffersResponse | undefined>(undefined);
  const [unexpectedError, setUnexpectedError] = useState<ErrorResult | null>(null);

  useEffect(() => {
    if (!id) {
      return;
    }

    getExtendedOffersById(id)
      .then((response) => {
        setOffers(response);
      })
      .catch((error: any) => {
        if (error instanceof HttpError) {
          switch (error.statusCode) {
            case statusCode.validation_error:
              setUnexpectedError({
                code: error.statusCode.toString(),
                message: error.message
              });
              break;
            case statusCode.authentication_error:
              localStorage.removeItem(AUTH_TOKEN);
              window.location.href = SIGN_IN_PATH;
              return;
            case statusCode.unexpected_error:
              setUnexpectedError(customErrorMessage);
              break;
            default:
              setUnexpectedError(customErrorMessage);
              break;
          }
        } else {
          setUnexpectedError(customErrorMessage);
        }
        console.error('Failed to fetch offers:', error);
      });
  }, [id]);

  if (!offers) {
    return (
      <Container>
        <h1>Join Offer </h1>
        <PageTile>
          <Container justify={'center'} align={'center'}>
            <LoadingContainer>
              <SkeletonLoader height={'100px'} />
              <SkeletonLoader height={'100px'} />
              <SkeletonLoader height={'100px'} />
            </LoadingContainer>
          </Container>
        </PageTile>
      </Container>
    );
  }

  if (unexpectedError) {
    return (
      <ErrorWindow
        withModal
        title={unexpectedError.code}
        information={unexpectedError.message}
        icon={<SvgIcon src={NotFoundIcon} width={219} height={125} />}
      />
    );
  }

  return (
    <Container>
      <Breadcrumbs pageTitle={'Deal settings'} breadcrumbsTitle={'Explore traffic offers'} />
      <PageTile>
        <FormContainer justify={'center'} align={'center'}>
          <DealsRequestForm offer={offers}></DealsRequestForm>
        </FormContainer>
      </PageTile>
    </Container>
  );
};
